// libs
import React, { memo, useEffect, useState } from 'react';
import { useLocation, Switch } from 'react-router';
import { Route } from 'react-router-dom';

// global components
import AppLayoutMobile from 'shared/layouts/AppLayout/Mobile';
import AppLayoutDesktop from 'shared/layouts/AppLayout/Desktop';
import RouteChunkRenderer from 'shared/components/RouteChunkRenderer';
import BottomNav from 'shared/components/BottomNavV2';
import PageTransition from 'shared/components/PageTransition';
import SmartProductJourney from 'shared/components/SmartProductJourney/SmartProductJourney';
import { AppMigrationBanner, AppMigrationDirectBottomSheet } from 'shared/components/AppMigration';

import { routesWithAppLayout, routesWithoutAppLayout } from './routeConfig';

import customRouteProp from './CustomRouteProp';

const PDP_PATTERNS = [/^\/detail\//, /\/p\//, /^\/catalog\/product\/view\/id\//];
const LISTING_PATTERNS = [
  /\/c\//,
  /^\/catalogsearch\/result/,
  /^\/lp\//,
  /^\/lp-sdk\//,
  /^\/op\//,
  /^\/widget-listing\//,
  /^\/dp\//,
];

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// webmcp tools
import ProductSuggestionTool from 'shared/layouts/DetailsPage/components/ProductDetail/ProductSuggestionTool';
import OpenCartTool from 'shared/layouts/DetailsPage/components/ProductDetail/OpenCartTool';

function ScrollManager() {
  const [currentPath, setCurrentPath] = useState('');
  const location = useLocation();

  useEffect(() => {
    // Scroll to window top if location path changed
    if (location.pathname !== currentPath) {
      window.scrollTo(0, 0);
      setCurrentPath(location.pathname);
    }
  }, [location, currentPath]);

  return null;
}

function RouteWithSubRoutes(route: any) {
  return (
    <Route
      path={route.path}
      exact={route.exact}
      render={customRouteProp(route.component, route.routes)}
    />
  );
}

function BaseRouteLayout() {
  const { enabled: mcpEnabled, feature: mcpFeature } = useRemoteConfig('webMcpFeature');

  const RoutesWithoutLayout = routesWithoutAppLayout.map((route, i: number) => (
    <RouteWithSubRoutes key={`route-without-layout-${route.path || i}`} {...route} />
  ));

  const RoutesWithAppLayout = routesWithAppLayout.map((route, i: number) => (
    <RouteWithSubRoutes key={`route-with-layout-${route.path || i}`} {...route} />
  ));

  return (
    <>
      <RouteChunkRenderer
        mobileComponent={
          <SmartProductJourney>
            <PageTransition patterns={PDP_PATTERNS} fromPatterns={LISTING_PATTERNS}>
              <Switch>
                {RoutesWithoutLayout}
                <AppLayoutMobile>
                  <Switch>{RoutesWithAppLayout}</Switch>
                  <AppMigrationBanner />
                  <AppMigrationDirectBottomSheet />
                </AppLayoutMobile>
              </Switch>
            </PageTransition>
            <BottomNav />
          </SmartProductJourney>
        }
        desktopComponent={
          <Switch>
            {RoutesWithoutLayout}
            <AppLayoutDesktop>
              <Switch>{RoutesWithAppLayout}</Switch>
              {mcpEnabled && mcpFeature.productSuggestion && <ProductSuggestionTool />}
              {mcpEnabled && <OpenCartTool />}
            </AppLayoutDesktop>
          </Switch>
        }
      />
      <ScrollManager />
    </>
  );
}

export default memo(BaseRouteLayout);
